"use client";

import {
  type FC,
  type Ref,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type AddCardOptionRecord,
  type UIAction,
  type AddCardProps,
  AddCard,
  useActions,
  useDebug,
  OptionalCard,
} from "@huub-web/ui";

import { buildApiRequestValueAction } from "../../utils";
import { useTemplates } from "../../hooks";
import { PageContentContext } from "../../contexts/PageContentContext";

export interface AddCardTemplateProps {
  title: string;
  description?: string;
  action?: UIAction;
  options?: (Omit<AddCardOptionRecord, "onClick"> & { action?: UIAction })[];
  optional?: {
    action: UIAction;
    label: string;
    value?: boolean;
  };
}

export const AddCardTemplate: FC<
  AddCardTemplateProps & { ref?: Ref<HTMLButtonElement> }
> = ({ title, description, action, options, optional, ref }) => {
  const { handleAction } = useActions();
  const { lockUI, lockPrompts } = useTemplates();
  const { log } = useDebug();
  const { registerAction, deregisterAction } = useContext(PageContentContext);

  const [isOptionalChecked, setIsOptionalChecked] = useState(
    optional?.value ?? false,
  );
  const [isOptionalLoading] = useState(false); // TODO
  const [isLocked, setIsLocked] = useState(false);

  // Stable serialization keys to avoid re-running the effect when
  // options/action are new (but identical) object references each render.
  const optionsKey = useMemo(
    () =>
      options
        ? JSON.stringify(options.map((o) => ({ id: o.id, label: o.label })))
        : "",
    [options],
  );
  const actionKey = useMemo(
    () => (action ? JSON.stringify(action) : ""),
    [action],
  );
  const optionalKey = useMemo(
    () => (optional?.action ? JSON.stringify(optional.action) : ""),
    [optional?.action],
  );

  // Register page actions for agent awareness
  useEffect(() => {
    const ids: string[] = [];

    if (options && options.length > 0) {
      // Multiple subtypes (e.g., Loondienst, Zelfstandig, Onderneming)
      for (const opt of options) {
        if (opt.action) {
          const id = `addcard-${title}-${opt.label ?? opt.id}`;
          const label = opt.label ? `Toevoegen: ${opt.label}` : title;
          registerAction(id, { id, label, action: opt.action });
          ids.push(id);
        }
      }
    } else if (action) {
      // Single action
      const id = `addcard-${title}`;
      registerAction(id, { id, label: title, action });
      ids.push(id);
    }

    if (optional?.action) {
      const id = `addcard-${title}-optional`;
      registerAction(id, {
        id,
        label: optional.label,
        action: optional.action,
      });
      ids.push(id);
    }

    return () => {
      for (const id of ids) {
        deregisterAction(id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    actionKey,
    optionsKey,
    optionalKey,
    registerAction,
    deregisterAction,
  ]);

  const handleClick = () => {
    if (action) {
      handleAction(action);

      if (action.type === "apiRequest") {
        setIsLocked(true);
      }
    }
  };

  const handleOptionalChange = (checked: boolean) => {
    setIsOptionalChecked(checked);
    if (!optional?.action) return;

    const optionalAction = buildApiRequestValueAction({
      action: optional.action,
      value: checked,
    });

    log("purple", "AddCardTemplate", "optional action", optionalAction);
    handleAction(optionalAction);
  };

  const mappedOptions: AddCardProps["options"] =
    options?.map(({ action, ...option }, index) => ({
      ...option,
      id: option.id ?? `option-${index}`,
      onClick: () => {
        log("purple", "AddCardTemplate", "option clicked", action);
        if (action) {
          handleAction(action);

          if (action.type === "apiRequest") {
            setIsLocked(true);
          }
        }
      },
    })) ?? [];

  return (
    <>
      {optional && (
        <OptionalCard
          onChange={handleOptionalChange}
          value={isOptionalChecked}
          label={optional.label}
          disabled={lockUI || isLocked}
          loading={isOptionalLoading}
        />
      )}
      <AddCard
        ref={ref}
        title={title}
        description={description}
        onClick={
          options && options.length > 0
            ? undefined
            : action
              ? handleClick
              : undefined
        }
        disabled={
          lockUI ||
          isLocked ||
          (lockPrompts && action?.type === "prompt") ||
          isOptionalChecked
        }
        options={mappedOptions}
        expandedOptions
      />
    </>
  );
};
